import { getCookie } from 'h3';

/**
 * Light/dark theming for the event app.
 *
 * - `data-theme` ('light' | 'dark') on <html> selects the CSS variable set defined
 *   in assets/styles/_themes.scss.
 * - `themeOption` ('light' | 'dark' | 'system') is the user's explicit choice; 'system'
 *   follows the OS via `prefers-color-scheme` and reacts to live changes.
 * - The cookie is the single source of truth so the choice survives reloads and is
 *   read on the server to avoid a flash of the wrong theme.
 *
 * Exposed as `$theme` ({ set, current, currentOption }) for the mode switcher.
 */
type Theme = 'light' | 'dark';
type ThemeOption = Theme | 'system';

const THEME_COLORS: Record<Theme, string> = {
  light: '#ffffff',
  dark: '#0c121a',
};

export default defineNuxtPlugin((nuxtApp) => {
  const theme = ref<Theme>('light');
  const themeOption = ref<ThemeOption>('system');
  let systemMatchMedia: MediaQueryList | null = null;

  // Server-side: seed from cookies so the initial HTML already has the right data-theme.
  if (import.meta.server) {
    const event = useRequestEvent();
    if (event) {
      const cookieTheme = getCookie(event, 'theme');
      if (cookieTheme === 'light' || cookieTheme === 'dark') {
        theme.value = cookieTheme;
      }
      const cookieThemeOption = getCookie(event, 'themeOption');
      if (cookieThemeOption === 'light' || cookieThemeOption === 'dark' || cookieThemeOption === 'system') {
        themeOption.value = cookieThemeOption;
      }
      useHead({ htmlAttrs: { 'data-theme': theme.value } });
    }

    nuxtApp.provide('theme', {
      set: () => {},
      current: theme,
      currentOption: themeOption,
    });
  }

  // Client-side: sync to the DOM, persist, and track the system preference.
  if (import.meta.client) {
    const hostParts = window.location.hostname.split('.');
    const rootDomain = hostParts.length > 1 ? '.' + hostParts.slice(-2).join('.') : window.location.hostname;

    const applyThemeColorMeta = (t: Theme) => {
      let meta = document.querySelector('meta[name="theme-color"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'theme-color');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', THEME_COLORS[t]);
    };

    const setTheme = (option: ThemeOption) => {
      themeOption.value = option;
      const resolved: Theme = option === 'system'
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : option;
      theme.value = resolved;
      document.documentElement.setAttribute('data-theme', resolved);
      applyThemeColorMeta(resolved);
      document.cookie = `theme=${resolved};path=/;domain=${rootDomain};max-age=31536000`;
      document.cookie = `themeOption=${option};path=/;domain=${rootDomain};max-age=31536000`;
    };

    const onSystemChange = (e: MediaQueryListEvent) => {
      if (themeOption.value === 'system') {
        const systemTheme: Theme = e.matches ? 'dark' : 'light';
        if (theme.value !== systemTheme) setTheme('system');
      }
    };

    systemMatchMedia = window.matchMedia('(prefers-color-scheme: dark)');
    systemMatchMedia.addEventListener('change', onSystemChange);
    window.addEventListener('beforeunload', () => {
      systemMatchMedia?.removeEventListener('change', onSystemChange);
    });

    // Cookie is the source of truth (localStorage is not shared across subdomains).
    const stored = document.cookie.replace(
      /(?:(?:^|.*;\s*)themeOption\s*=\s*([^;]*).*$)|^.*$/,
      '$1',
    ) as ThemeOption | '';
    setTheme(stored || 'system');

    nuxtApp.provide('theme', {
      set: setTheme,
      current: theme,
      currentOption: themeOption,
    });
  }
});
